<script lang="ts">
  import { imask } from "@imask/svelte";

  export let placeholder: string;
  export let onChange: ((value: string | undefined | boolean, event?: KeyboardEvent) => void) | undefined = undefined;
  export let isLoading: boolean | undefined = undefined;
  export let isDisabled: boolean = false;
  export let value: string | boolean | undefined = "";
  export let invalidMessage: string | undefined = undefined;
  export let mask: string | undefined = undefined;
  export let type: "text" | "email" | "tel" | "number" | "date" = "text";
  export let inputProps: Record<string, unknown> = {};
  export let handleOnBlurEvent = false;
  export let handleOnEnterKeyPress = false;

  const handleChange = (e: Event) => {
    value = (e.target as HTMLInputElement).value;
    onChange && onChange(value, e as KeyboardEvent);
  };

  const onComplete = ({ detail }: { detail: { _value: string } }) => {
    // If we set to the unmasked value, then as soon as it's complete, we lose the masking.
    value = detail._value;

    onChange && onChange(value);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && handleOnEnterKeyPress) {
      onChange && onChange(value, e);
    }
  };

  $: showLoadingIndicator = isLoading;
  $: mergedClass = `relative ${$$restProps.class || ""}`;

  let restProps = { ...$$restProps };
  delete restProps?.class;

  let dateInput: HTMLInputElement | undefined;
</script>

<div class={mergedClass} {...restProps}>
  {#if showLoadingIndicator}
    <!-- TODO: replace spinner -->
    <div class="absolute flex items-center justify-center top-6 left-4">
      <svg
        class="animate-spin h-5 w-5 text-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V2.5"></path>
      </svg>
    </div>
  {/if}
  {#if type === "text"}
    <input
      type="text"
      class:invalid-input={!!invalidMessage}
      class:is-loading={showLoadingIndicator}
      disabled={isDisabled}
      class="w-full"
      data-testid={placeholder}
      aria-label={placeholder}
      {...inputProps}
      bind:value
      on:input={mask ? undefined : handleChange}
      on:complete={mask ? onComplete : undefined}
      on:blur={handleOnBlurEvent ? handleChange : undefined}
      on:keydown={onKeyDown}
      use:imask={mask ? { mask, lazy: false } : undefined} />
  {:else if type === "number"}
    <input
      type="number"
      class:invalid-input={!!invalidMessage}
      class:is-loading={showLoadingIndicator}
      disabled={isDisabled}
      class="w-full"
      data-testid={placeholder}
      aria-label={placeholder}
      {...inputProps}
      bind:value
      on:input={mask ? undefined : handleChange}
      on:complete={mask ? onComplete : undefined}
      use:imask={mask ? { mask, lazy: false } : undefined} />
  {:else if type === "email"}
    <input
      type="email"
      class:invalid-input={!!invalidMessage}
      class:is-loading={showLoadingIndicator}
      disabled={isDisabled}
      class="w-full"
      data-testid={placeholder}
      aria-label={placeholder}
      {...inputProps}
      bind:value
      on:input={mask ? undefined : handleChange}
      on:complete={mask ? onComplete : undefined}
      use:imask={mask ? { mask, lazy: false } : undefined} />
  {:else if type === "tel"}
    <input
      type="tel"
      class:invalid-input={!!invalidMessage}
      class:is-loading={showLoadingIndicator}
      disabled={isDisabled}
      class="w-full"
      data-testid={placeholder}
      aria-label={placeholder}
      {...inputProps}
      bind:value
      on:input={mask ? undefined : handleChange}
      on:complete={mask ? onComplete : undefined}
      use:imask={mask ? { mask, lazy: false } : undefined} />
  {:else if type === "date"}
    <input
      type="date"
      class:invalid-input={!!invalidMessage}
      class:is-loading={showLoadingIndicator}
      disabled={isDisabled}
      class="w-full"
      data-testid={placeholder}
      aria-label={placeholder}
      {...inputProps}
      bind:value
      bind:this={dateInput}
      on:click={() => dateInput?.showPicker()}
      on:input={handleChange}
      on:complete={onComplete} />
  {/if}
  <span class="floating-label" class:small={String(value)?.trim()}>{placeholder}</span>
  {#if invalidMessage}
    <p class="invalid-message text-xs mt-1">{invalidMessage}</p>
  {/if}
</div>

<style>
  .invalid-input {
    border: 1px solid var(--color-red-20);
  }

  .invalid-message {
    color: var(--color-red-50);
  }

  .floating-label {
    position: absolute;
    top: 1rem;
    left: 1rem;
    transition: all 0.2s;
    color: var(--color-grayscale-70);
    pointer-events: none;
  }

  input:focus + .floating-label,
  .floating-label.small {
    top: 0.5rem;
    font-size: 0.75rem;
  }

  input {
    border: 1px solid var(--color-grayscale-20); /* same color border, so it doesn't jump on focus */
    outline: none;
    background-color: var(--color-grayscale-20);
    padding: 1.5rem 1rem 0.5rem 1rem;
    border-radius: var(--input-border-radius);
  }

  input.is-loading {
    padding-left: 2.5rem;
  }

  input:focus {
    border: 1px solid var(--color-grayscale-50);
  }
  input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    background-color: var(--color-grayscale-50);
    border-radius: 0.25rem;
    padding: 0.25rem;
  }
</style>
